import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface KnowledgePoint {
  title: string;
  detail: string;
  icon: string;
}

interface AcademicEducationProps {
  subject: string;
  subjectIcon: string;
  topic: string;
  subtitle: string;
  points: KnowledgePoint[];
  formula?: string;
  formulaExplain?: string;
  summary: string;
  accentColor?: string;
  accentColorLight?: string;
}

const DEFAULT_PROPS: AcademicEducationProps = {
  subject: '物理',
  subjectIcon: '⚛️',
  topic: '能量守恒定律',
  subtitle: 'Law of Conservation of Energy',
  points: [
    { title: '定义', detail: '能量既不会凭空产生，也不会凭空消灭，只能从一种形式转化为另一种形式', icon: '📖' },
    { title: '动能', detail: 'Ek = ½mv²，与物体质量和速度的平方成正比', icon: '🏃' },
    { title: '势能', detail: 'Ep = mgh，与物体质量、重力加速度和高度成正比', icon: '⛰️' },
    { title: '转化', detail: '动能和势能可以相互转化，总机械能保持不变', icon: '🔄' },
  ],
  formula: 'Ek₁ + Ep₁ = Ek₂ + Ep₂',
  formulaExplain: '初状态总能量 = 末状态总能量',
  summary: '能量守恒是物理学最基本的定律之一',
  accentColor: '#667eea',
  accentColorLight: 'rgba(102, 126, 234, 0.15)',
};

const FloatingParticle: React.FC<{ delay: number; x: number; size: number }> = ({ delay, x, size }) => {
  const frame = useCurrentFrame();
  const y = interpolate((frame + delay) % 200, [0, 200], [1920, -50]);
  const opacity = interpolate((frame + delay) % 200, [0, 40, 160, 200], [0, 0.3, 0.3, 0]);
  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.4)',
        opacity,
        filter: `blur(${size / 4}px)`,
      }}
    />
  );
};

export const AcademicEducationTemplate: React.FC<Partial<AcademicEducationProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const accent = p.accentColor!;
  const accentLight = p.accentColorLight!;

  // Phase 1: Opening (0-60)
  const titleScale = spring({ frame, fps, config: { damping: 14, stiffness: 120, mass: 0.8 } });
  const titleOpacity = interpolate(frame, [0, 20], [0, 1], { extrapolateRight: 'clamp' });
  const badgeX = spring({ frame: frame - 5, fps, from: -200, to: 0, config: { damping: 18, stiffness: 100 } });
  const subtitleOpacity = interpolate(frame, [25, 50], [0, 1], { extrapolateRight: 'clamp' });
  const lineWidth = interpolate(frame, [30, 70], [0, 600], { extrapolateRight: 'clamp' });

  // Phase 2: Knowledge points (70-300)
  const pointsStart = 70;
  const pointDuration = 50;

  // Phase 3: Formula (310-400)
  const formulaProgress = spring({
    frame: frame - 310,
    fps,
    from: 0,
    to: 1,
    config: { damping: 12, stiffness: 100, mass: 0.8 },
  });
  const formulaOpacity = interpolate(frame, [310, 330], [0, 1], { extrapolateRight: 'clamp' });

  // Phase 4: Summary (410-480)
  const summaryOpacity = interpolate(frame, [410, 435], [0, 1], { extrapolateRight: 'clamp' });
  const summaryY = interpolate(frame, [410, 440], [40, 0], { extrapolateRight: 'clamp' });

  const particles = Array.from({ length: 12 }, (_, i) => ({
    delay: i * 25,
    x: (i * 97) % 1080,
    size: 4 + (i % 3) * 3,
  }));

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(170deg, #0a0a1a 0%, #111133 40%, #1a1a4e 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Helvetica Neue", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {particles.map((pt, i) => (
        <FloatingParticle key={i} {...pt} />
      ))}

      {/* decorative grid */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Subject badge */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          left: 60,
          transform: `translateX(${badgeX}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          background: accentLight,
          border: `2px solid ${accent}`,
          borderRadius: 50,
          padding: '12px 32px',
        }}
      >
        <span style={{ fontSize: 36 }}>{p.subjectIcon}</span>
        <span style={{ fontSize: 42, fontWeight: 600, color: accent }}>{p.subject}</span>
      </div>

      {/* Title */}
      <div
        style={{
          position: 'absolute',
          top: 180,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: titleOpacity,
          transform: `scale(${titleScale})`,
        }}
      >
        <h1
          style={{
            fontSize: 72,
            fontWeight: 800,
            margin: 0,
            letterSpacing: '-0.02em',
            background: `linear-gradient(135deg, ${accent}, #a78bfa, #f472b6)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 36,
            marginTop: 16,
            opacity: subtitleOpacity,
            color: 'rgba(255,255,255,0.6)',
            fontStyle: 'italic',
          }}
        >
          {p.subtitle}
        </p>
        <div
          style={{
            width: lineWidth,
            height: 3,
            background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
            margin: '24px auto 0',
          }}
        />
      </div>

      {/* Knowledge points */}
      <div
        style={{
          position: 'absolute',
          top: 450,
          left: 60,
          right: 60,
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
        }}
      >
        {p.points.map((point, i) => {
          const startFrame = pointsStart + i * pointDuration;
          const cardProgress = spring({
            frame: frame - startFrame,
            fps,
            from: 0,
            to: 1,
            config: { damping: 16, stiffness: 120, mass: 0.6 },
          });
          const cardOpacity = interpolate(frame, [startFrame, startFrame + 15], [0, 1], { extrapolateRight: 'clamp' });
          const cardX = interpolate(frame, [startFrame, startFrame + 20], [80, 0], { extrapolateRight: 'clamp' });

          const numberProgress = interpolate(frame, [startFrame, startFrame + 25], [0, 1], { extrapolateRight: 'clamp' });

          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 20,
                opacity: cardOpacity,
                transform: `translateX(${cardX}px) scale(${0.95 + cardProgress * 0.05})`,
              }}
            >
              {/* step number */}
              <div
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 16,
                  background: `linear-gradient(135deg, ${accent}, #a78bfa)`,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: 42,
                  fontWeight: 700,
                  flexShrink: 0,
                  transform: `scale(${numberProgress})`,
                }}
              >
                {point.icon}
              </div>

              {/* card */}
              <div
                style={{
                  flex: 1,
                  background: 'rgba(255,255,255,0.06)',
                  borderRadius: 20,
                  padding: '24px 28px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <div
                  style={{
                    fontSize: 38,
                    fontWeight: 700,
                    color: accent,
                    marginBottom: 8,
                  }}
                >
                  {point.title}
                </div>
                <div
                  style={{
                    fontSize: 32,
                    lineHeight: 1.5,
                    color: 'rgba(255,255,255,0.8)',
                  }}
                >
                  {point.detail}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Formula highlight */}
      {p.formula && (
        <div
          style={{
            position: 'absolute',
            top: 1300,
            left: 60,
            right: 60,
            opacity: formulaOpacity,
            transform: `scale(${0.9 + formulaProgress * 0.1})`,
          }}
        >
          <div
            style={{
              background: `linear-gradient(135deg, ${accentLight}, rgba(167, 139, 250, 0.15))`,
              border: `2px solid ${accent}`,
              borderRadius: 24,
              padding: '36px 40px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 40, color: 'rgba(255,255,255,0.5)', marginBottom: 12, fontWeight: 500 }}>
              核心公式
            </div>
            <div
              style={{
                fontSize: 52,
                fontWeight: 700,
                fontFamily: '"Times New Roman", Georgia, serif',
                fontStyle: 'italic',
                color: 'white',
                letterSpacing: '0.03em',
              }}
            >
              {p.formula}
            </div>
            {p.formulaExplain && (
              <div style={{ fontSize: 36, marginTop: 16, color: 'rgba(255,255,255,0.6)' }}>
                {p.formulaExplain}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary */}
      <div
        style={{
          position: 'absolute',
          bottom: 60,
          left: 60,
          right: 60,
          textAlign: 'center',
          opacity: summaryOpacity,
          transform: `translateY(${summaryY}px)`,
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 12,
            background: 'rgba(255,255,255,0.08)',
            borderRadius: 50,
            padding: '16px 36px',
            border: '1px solid rgba(255,255,255,0.15)',
          }}
        >
          <span style={{ fontSize: 34 }}>💡</span>
          <span style={{ fontSize: 34, color: 'rgba(255,255,255,0.9)' }}>{p.summary}</span>
        </div>
      </div>

      {/* progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: `linear-gradient(90deg, ${accent}, #a78bfa)`,
        }}
      />
    </AbsoluteFill>
  );
};
